/**
 * Admin · Users directory — reusable list for Parents and Staff sub-pages.
 * The page wrappers below set the role filter + UI labels.
 */
import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { useAdminWorkspace, AdminHeader, AdminEmpty, AdminEmptyCard } from "./_shared";

function AdminUsersTable({ eyebrow, title, roleFilter, testidPrefix, firstTimeBody, firstTimeAction }) {
  const { workspace, loading: wsLoading } = useAdminWorkspace();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!workspace) return;
    let alive = true;
    const url = `/admin/workspaces/${workspace.id}/users${
      roleFilter ? `?role_filter=${roleFilter}` : ""
    }`;
    api
      .get(url)
      .then((r) => alive && setUsers(r.data || []))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [workspace?.id, roleFilter]);

  if (wsLoading) return null;
  if (!workspace)
    return (
      <AdminEmptyCard
        testid={`${testidPrefix}-no-ws`}
        eyebrow={eyebrow}
        title="A workspace first."
        body={`We'll show your ${title} as soon as you've created your center.`}
        action={
          <a href="/workspace" data-testid={`${testidPrefix}-create-workspace`} className="btn-primary">
            Create a workspace
          </a>
        }
      />
    );

  return (
    <div data-testid={`${testidPrefix}-screen`}>
      <AdminHeader eyebrow={eyebrow} title={`${users.length} ${title}`} />
      {loading ? (
        <AdminEmpty testid={`${testidPrefix}-loading`}>A quiet moment…</AdminEmpty>
      ) : users.length === 0 ? (
        <AdminEmptyCard
          testid={`${testidPrefix}-empty`}
          eyebrow="Invitations"
          title="No one here yet."
          body={firstTimeBody}
          action={firstTimeAction}
        />
      ) : (
        <ul
          data-testid={`${testidPrefix}-list`}
          className="mt-6 rounded-3xl bg-white border hairline overflow-hidden divide-y divide-[#171717]/6"
        >
          {users.map((u) => (
            <li
              key={u.id}
              data-testid={`${testidPrefix}-row-${u.id}`}
              className="flex items-center gap-4 px-5 py-4"
            >
              <div className="h-10 w-10 rounded-full bg-[#F2EFEA] flex items-center justify-center font-serif italic text-[#8A9984] shrink-0">
                {(u.name || "—")[0]}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-serif text-lg text-[#171717] truncate">
                  {u.name || "—"}
                </div>
                <div className="text-xs text-[#525252] mt-0.5 truncate">
                  {u.email}
                </div>
              </div>
              <div className="hidden sm:flex flex-col items-end gap-1">
                <span className="trust-pill" data-testid={`${testidPrefix}-row-${u.id}-role`}>
                  {u.role}
                </span>
                {u.linked_children > 0 && (
                  <span className="text-[10px] tracking-[0.18em] uppercase text-[#525252]">
                    {u.linked_children}{" "}
                    {u.linked_children === 1 ? "child" : "children"}
                  </span>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function AdminParents() {
  return (
    <AdminUsersTable
      eyebrow="Parents"
      title="parents in your community"
      roleFilter="parent"
      testidPrefix="admin-parents"
      firstTimeBody="Parents arrive when their first child is added and a co-parent invitation is sent. Once they accept, you'll see them gather here."
      firstTimeAction={
        <a href="/workspace" data-testid="admin-parents-add-child" className="btn-primary">
          Open workspace
        </a>
      }
    />
  );
}

export function AdminStaff() {
  // For staff we want both caregivers AND admins. The endpoint accepts a single
  // role filter, so we'd ideally call twice — but listing without a filter and
  // excluding parents client-side is simpler and equally correct for v1.
  const { workspace } = useAdminWorkspace();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!workspace) return;
    let alive = true;
    api
      .get(`/admin/workspaces/${workspace.id}/users`)
      .then((r) =>
        alive &&
        setUsers((r.data || []).filter((u) => u.role !== "parent"))
      )
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [workspace?.id]);

  if (!workspace)
    return (
      <AdminEmptyCard
        testid="admin-staff-no-ws"
        eyebrow="Staff"
        title="A workspace first."
        body="Once you've created your center you can invite caregivers and other admins to join."
        action={
          <a href="/workspace" data-testid="admin-staff-create-workspace" className="btn-primary">
            Create a workspace
          </a>
        }
      />
    );
  return (
    <div data-testid="admin-staff-screen">
      <AdminHeader eyebrow="Staff" title={`${users.length} on your team`} />
      {loading ? (
        <AdminEmpty testid="admin-staff-loading">A quiet moment…</AdminEmpty>
      ) : users.length === 0 ? (
        <AdminEmptyCard
          testid="admin-staff-empty"
          eyebrow="Invitations"
          title="No staff yet."
          body="Invite caregivers from your workspace settings. They'll join with calm permissions, and you can add more admins as your team grows."
          action={
            <a href="/workspace" data-testid="admin-staff-invite" className="btn-primary">
              Open workspace
            </a>
          }
        />
      ) : (
        <ul
          data-testid="admin-staff-list"
          className="mt-6 rounded-3xl bg-white border hairline overflow-hidden divide-y divide-[#171717]/6"
        >
          {users.map((u) => (
            <li
              key={u.id}
              data-testid={`admin-staff-row-${u.id}`}
              className="flex items-center gap-4 px-5 py-4"
            >
              <div className="h-10 w-10 rounded-full bg-[#F2EFEA] flex items-center justify-center font-serif italic text-[#8A9984] shrink-0">
                {(u.name || "—")[0]}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-serif text-lg text-[#171717] truncate">
                  {u.name || "—"}
                </div>
                <div className="text-xs text-[#525252] mt-0.5 truncate">
                  {u.email}
                </div>
              </div>
              <span
                className="trust-pill"
                data-testid={`admin-staff-row-${u.id}-role`}
                style={{
                  background: u.role === "admin" ? "#1A1B2E" : "#F2EFEA",
                  color: u.role === "admin" ? "#FBF9F6" : "#525252",
                }}
              >
                {u.role}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
